import { useState } from 'react'
import { ActivityEvent as ActivityEventType, ActivityEventProps, BaseActivityEventProps } from './Types'
import { TimelineEventType } from 'screens/Activity/Types'
import { CompleteTodo } from './CompleteTodo'
import { EditsGroup } from './EditsGroup'
import { CommentsGroup } from './CommentsGroup'
import { ArticleSuggestionReviewsCreated } from './ArticleSuggestionReviewsCreated'
import { ArticleSuggestionReviewRejected } from './ArticleSuggestionReviewRejected'
import { ArticleSuggestionApprovedAndMerged } from './ArticleSuggestionApprovedAndMerged'
import { ArticleSuggestionCancelled } from './ArticleSuggestionCancelled'
import { ArticleInformalFeedbackReviewsCreated } from './ArticleInformalFeedbackReviewsCreated'
import { ArticleInformalFeedbackReviewSubmitted } from './ArticleInformalFeedbackReviewSubmitted'
import { ArticleInformalFeedbackReviewDismissed } from './ArticleInformalFeedbackReviewDismissed'
import { ArticleInformalFeedbackRequestClosed } from './ArticleInformalFeedbackRequestClosed'
import { ArticleReviewRequest } from './ArticleReviewRequest'
import { ArticleReviewRejection } from './ArticleReviewRejection'
import { ArticleReviewRequestApprovedAndMerged } from './ArticleReviewRequestApprovedAndMerged'
import { ArticleReviewCancel } from './ArticleReviewCancel'
import { ArticleReadReceiptRequest } from './ArticleReadReceiptRequest'
import { ArticleEndReadReceipt } from './ArticleEndReadReceipt'
import { ArticleMarkedAsRead } from './ArticleMarkedAsRead'
import { ArticleAllTasksMarkedAsRead } from './ArticleAllTasksMarkedAsRead'
import { ArticleBranchCreated } from './ArticleBranchCreated'
import { ArticleMerged } from './ArticleMerged'
import { ArticleMergedInto } from './ArticleMergedInto'
import { ArticleCreated } from './ArticleCreated'
import { ArticleCopy } from './ArticleCopy'
import { ArticleLastViewed } from './ArticleLastViewed'
import { GrantArticleAccess } from './GrantArticleAccess'
import { ArticleEventRestored } from './ArticleEventRestored'
import { CreateTodo } from './CreateTodo'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { IconButton } from 'components/IconButton/IconButton'
import { ReactComponent as ThreeDotsIcon } from 'assets/icons/threeDots.svg'
import { Menu } from 'components/Menu/Menu'
import { useActivityActionModalContext } from 'screens/Activity/components/ActionModal/ActivityActionModalContext'
import { createActionHandlers, defaultHandlers, copyAction, renameAction, restoreAction } from './ActionMenu'
import { EventRenamingInput } from './EventRenamingInput'
import { getDisabledActionsForEvent } from './utils'
import { useArticleMeta } from 'hooks/useArticleMeta'
import { ArticleInformalFeedbackViewersCreated } from './ArticleInformalFeedbackViewersCreated'
import { LayerDeleted } from './LayerDeleted'
import { LayerCreated } from './LayerCreated'
import { LayerMerged } from './LayerMerged'
import { WorkflowStatusUpdated } from './WorkflowStatusUpdated'
import { AlmanacFileVcmChanged } from './AlmanacFileVcmChanged'
import { useLocation } from 'react-router-dom'
import { defaultDateTimeFormat } from 'components/DateTime/DateTime'
import { ArticleAIGenerated } from './ArticleAIGenerated'
import { ArticleAIRevisionAccepted } from 'components/ActivityEvent/ArticleAIRevisionAccepted'

/*
 * ActivityEvent receives an event and renders the corresponding Event
 * component depending on the type of the event. This allows us to have complex
 * rendering logic encapsulated in a dedicated component for each type of
 * Activity Event, while keeping the api consistent.
 */
const ActivityEvent = ({ event, ...otherProps }: ActivityEventProps) => {
  const { state } = useLocation<{ timeline?: { flashEvent?: string } }>()
  const articleMeta = useArticleMeta(event.article.key)
  const actionModalContext = useActivityActionModalContext()
  const [renaming, setRenaming] = useState(false)

  const actionHandlers = createActionHandlers(actionModalContext, event, {
    ...defaultHandlers,
    rename: () => setRenaming(true),
  })

  const disabledActions = getDisabledActionsForEvent(event, articleMeta)

  const defaultActions = (
    <MenuTrigger placement="bottom-end">
      <IconButton variant="secondaryGrey" icon={ThreeDotsIcon} aria-label="Activity Event Menu" />
      <Menu onAction={actionHandlers} disabledKeys={disabledActions}>
        {copyAction()}
        {renameAction()}
        {restoreAction()}
      </Menu>
    </MenuTrigger>
  )

  const name = renaming ? (
    <EventRenamingInput event={event} onCancel={() => setRenaming?.(false)} onEnter={() => setRenaming?.(false)} />
  ) : (
    event.name
  )

  const props = {
    ...otherProps,
    onClick: (mouseEvent: any) => {
      otherProps.onClick?.(mouseEvent, event)
    },
    name,
    actions: defaultActions,
    flashOnAppear: event.isNew || state?.timeline?.flashEvent === event.id,
    timestampFormat: otherProps.context === 'review' ? 'll' : defaultDateTimeFormat,
  }

  return <ActivityEventSwitch event={event} {...props} />
}

export const ActivityEventSwitch = ({
  event,
  ...props
}: Partial<BaseActivityEventProps> & { event: ActivityEventType; isTruncated?: boolean; openWizard?: boolean }) => {
  switch (event.type) {
    case TimelineEventType.EditsGroup: {
      return <EditsGroup event={event} {...props} />
    }
    case TimelineEventType.CommentsGroup:
    case TimelineEventType.RepliesGroup: {
      return <CommentsGroup event={event} {...props} />
    }
    case TimelineEventType.ArticleSuggestionReviewsCreated: {
      return <ArticleSuggestionReviewsCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleSuggestionReviewRejected: {
      return <ArticleSuggestionReviewRejected event={event} {...props} />
    }
    case TimelineEventType.ArticleSuggestionApprovedAndMerged: {
      return <ArticleSuggestionApprovedAndMerged event={event} {...props} />
    }
    case TimelineEventType.ArticleSuggestionCancelled: {
      return <ArticleSuggestionCancelled event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackReviewsCreated: {
      return <ArticleInformalFeedbackReviewsCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackViewersCoCreated: {
      return <ArticleInformalFeedbackViewersCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackViewersCreated: {
      return <ArticleInformalFeedbackViewersCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackReviewSubmitted: {
      return <ArticleInformalFeedbackReviewSubmitted event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackReviewDismissed: {
      return <ArticleInformalFeedbackReviewDismissed event={event} {...props} />
    }
    case TimelineEventType.ArticleInformalFeedbackRequestClosed: {
      return <ArticleInformalFeedbackRequestClosed event={event} {...props} />
    }
    case TimelineEventType.ArticleReviewRequest: {
      return <ArticleReviewRequest event={event} {...props} />
    }
    case TimelineEventType.ArticleReviewRejection: {
      return <ArticleReviewRejection event={event} {...props} />
    }
    case TimelineEventType.ArticleReviewRequestApprovedAndMerged: {
      return <ArticleReviewRequestApprovedAndMerged event={event} {...props} />
    }
    case TimelineEventType.ArticleReviewCancel: {
      return <ArticleReviewCancel event={event} {...props} />
    }
    case TimelineEventType.ArticleReadReceiptRequest: {
      return <ArticleReadReceiptRequest event={event} {...props} />
    }
    case TimelineEventType.ArticleEndReadReceipt: {
      return <ArticleEndReadReceipt event={event} {...props} />
    }
    case TimelineEventType.ArticleMarkedAsRead: {
      return <ArticleMarkedAsRead event={event} {...props} />
    }
    case TimelineEventType.ArticleAllTasksMarkedAsRead: {
      return <ArticleAllTasksMarkedAsRead event={event} {...props} />
    }
    case TimelineEventType.ArticleBranchCreated: {
      return <ArticleBranchCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleMerged: {
      return <ArticleMerged event={event} {...props} />
    }
    case TimelineEventType.ArticleMergedInto: {
      return <ArticleMergedInto event={event} {...props} />
    }
    case TimelineEventType.ArticleCreated: {
      return <ArticleCreated event={event} {...props} />
    }
    case TimelineEventType.ArticleCopy: {
      return <ArticleCopy event={event} {...props} />
    }
    case TimelineEventType.ArticleLastViewed: {
      return <ArticleLastViewed event={event} {...props} />
    }
    case TimelineEventType.GrantArticleAccess: {
      return <GrantArticleAccess event={event} {...props} />
    }
    case TimelineEventType.ArticleEventRestored: {
      return <ArticleEventRestored event={event} {...props} />
    }
    case TimelineEventType.CreateTodo: {
      return <CreateTodo event={event} {...props} />
    }
    case TimelineEventType.CompleteTodo: {
      return <CompleteTodo event={event} {...props} />
    }
    case TimelineEventType.LayerCreated: {
      return <LayerCreated event={event} {...props} />
    }
    case TimelineEventType.LayerDeleted: {
      return <LayerDeleted event={event} {...props} />
    }
    case TimelineEventType.LayerMerged: {
      return <LayerMerged event={event} {...props} />
    }
    case TimelineEventType.WorkflowStatusUpdated: {
      return <WorkflowStatusUpdated event={event} {...props} />
    }
    case TimelineEventType.VcmChanged: {
      return <AlmanacFileVcmChanged event={event} {...props} />
    }
    case TimelineEventType.ArticleAIGeneratedEvent: {
      return <ArticleAIGenerated event={event} {...props} />
    }
    case TimelineEventType.ArticleAIRevisionAcceptedEvent: {
      return <ArticleAIRevisionAccepted event={event} {...props} />
    }
    default: {
      return null
    }
  }
}

export { ActivityEvent }
